import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';

const HEADER_ALIASES = { email: ['email', 'e-mail', 'mail', 'user email', 'learner email'], learner_id: ['learner_id', 'learner id', 'user id', 'user_id', 'employee id', 'employee_id', 'id'], name: ['name', 'full name', 'learner', 'user', 'display name'], cohort: ['cohort', 'cohort name', 'group', 'team', 'department', 'class', 'audience'], location: ['location', 'office', 'site', 'city', 'region', 'country'], tenure: ['tenure', 'tenure band', 'years of service', 'service years', 'experience'], start_date: ['start_date', 'start date', 'hire_date', 'hire date', 'joined', 'date joined'] };

// Parse a pasted LMS export (CSV or TSV, header row) into {email, learner_id, name, cohort, location?, tenure?, start_date?} rows.
export function parseLmsExport(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { rows: [], error: 'Paste at least a header row and one learner row.' };
  const delim = lines[0].includes('\t') ? '\t' : lines[0].includes(';') ? ';' : ',';
  const header = lines[0].split(delim).map((h) => h.trim().toLowerCase().replace(/^"|"$/g, ''));
  const col = (k) => header.findIndex((h) => HEADER_ALIASES[k].includes(h));
  const ix = Object.fromEntries(Object.keys(HEADER_ALIASES).map((k) => [k, col(k)]));
  if (ix.cohort === -1) return { rows: [], error: 'No cohort column found (accepted: cohort, group, team, department, class).' };
  if (ix.email === -1 && ix.learner_id === -1) return { rows: [], error: 'No email or learner id column found.' };
  const pick = (v, i) => (i >= 0 ? (v[i] || '').trim().replace(/^"|"$/g, '') : '');
  const rows = lines.slice(1).map((l) => l.split(delim)).map((v) => Object.fromEntries(Object.keys(HEADER_ALIASES).map((k) => [k, pick(v, ix[k])])));
  return { rows, error: null, columns: ix };
}

export default function LmsRosterSyncDialog({ open, onOpenChange, organizationId, onSynced }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const parsed = text.trim() ? parseLmsExport(text) : null;
  const cohortsInPaste = parsed?.rows ? [...new Set(parsed.rows.map((r) => r.cohort).filter(Boolean))] : [];
  const [preview, setPreview] = useState(null);

  // Live diff: dry-run the routing (debounced) so the user sees added / updated / unmatched before committing.
  useEffect(() => {
    if (!parsed || parsed.error || result) { setPreview(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const body = { rows: parsed.rows, dry_run: true };
        if (organizationId && organizationId !== 'all') body.organization_id = organizationId;
        const r = await api.post('/segments/cohorts/roster-sync', body);
        setPreview(r.data);
      } catch { setPreview(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [text, organizationId, result]); // eslint-disable-line react-hooks/exhaustive-deps

  const sync = async () => {
    if (!parsed || parsed.error) return;
    setBusy(true);
    try {
      const body = { rows: parsed.rows };
      if (organizationId && organizationId !== 'all') body.organization_id = organizationId;
      const r = await api.post('/segments/cohorts/roster-sync', body);
      setResult(r.data);
      toast.success(`Routed ${r.data.routed} learners into ${r.data.cohorts.length} cohort${r.data.cohorts.length === 1 ? '' : 's'}`);
      onSynced?.();
    } catch (err) { toast.error(`Sync failed: ${err.response?.data?.detail || err.message}`); }
    finally { setBusy(false); }
  };
  const reset = () => { setText(''); setResult(null); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl" data-testid="lms-sync-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><RefreshCw className="h-4 w-4" /> Sync rosters from LMS export</DialogTitle>
          <DialogDescription className="text-xs">Paste your LMS enrolment export (CSV or tab-separated, with a header row). Needs an <b>email</b> or <b>learner id</b> column plus a <b>cohort / group / team</b> column — learners are routed to the cohort whose name matches.</DialogDescription>
        </DialogHeader>
        {!result ? (
          <>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={9} placeholder={'email,name,cohort\nava.nguyen@acme.com,Ava Nguyen,Sales Department\nliam.patel@acme.com,Liam Patel,Customer Support'} className="w-full rounded-lg border border-slate-200 p-2 font-mono text-xs outline-none focus:border-teal-400" data-testid="lms-sync-textarea" />
            {parsed && (
              <p className={`text-xs ${parsed.error ? 'text-rose-600' : 'text-slate-600'}`} data-testid="lms-sync-preview">
                {parsed.error || `${parsed.rows.length} learner rows · ${cohortsInPaste.length} cohort name${cohortsInPaste.length === 1 ? '' : 's'}: ${cohortsInPaste.slice(0, 6).join(', ')}${cohortsInPaste.length > 6 ? '…' : ''}`}
              </p>
            )}
            {preview && !parsed?.error && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-1.5" data-testid="lms-sync-diff">
                <p className="font-semibold text-slate-700">What will happen</p>
                <div className="flex gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-emerald-700" data-testid="lms-sync-diff-added"><CheckCircle2 className="h-3.5 w-3.5" /> {preview.cohorts.reduce((n, c) => n + c.added, 0)} to add</span>
                  <span className="text-sky-700" data-testid="lms-sync-diff-updated">{preview.cohorts.reduce((n, c) => n + c.updated, 0)} already on roster (update)</span>
                  <span className={preview.unmatched_cohorts.length ? 'text-amber-800' : 'text-slate-500'} data-testid="lms-sync-diff-unmatched">{preview.unmatched_cohorts.reduce((n, u) => n + u.rows, 0)} unmatched</span>
                  {preview.skipped > 0 && <span className="text-slate-500">{preview.skipped} skipped</span>}
                </div>
                <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white">
                  {preview.cohorts.map((c) => (
                    <li key={c.cohort_id} className="flex items-center justify-between px-2.5 py-1" data-testid={`lms-sync-diff-cohort-${c.cohort_id}`}><span className="font-medium text-slate-800">{c.cohort_name}</span><span className="tabular-nums text-slate-600">+{c.added} · ↻{c.updated}</span></li>
                  ))}
                  {preview.unmatched_cohorts.map((u) => (
                    <li key={u.name} className="flex items-center justify-between px-2.5 py-1 bg-amber-50" data-testid={`lms-sync-diff-unmatched-${u.name}`}><span className="inline-flex items-center gap-1 text-amber-900"><AlertTriangle className="h-3 w-3" /> {u.name}</span><span className="text-amber-800 tabular-nums">{u.rows} row{u.rows === 1 ? '' : 's'} · no matching cohort</span></li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-2" data-testid="lms-sync-result">
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-xs">
              {result.cohorts.map((c) => (
                <li key={c.cohort_id} className="flex items-center justify-between px-3 py-1.5" data-testid={`lms-sync-cohort-${c.cohort_id}`}>
                  <span className="inline-flex items-center gap-1.5 font-medium text-slate-800"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />{c.cohort_name}</span>
                  <span className="text-slate-600 tabular-nums">{c.added} added · {c.updated} updated</span>
                </li>
              ))}
              {result.cohorts.length === 0 && <li className="px-3 py-3 text-slate-500">No rows could be routed.</li>}
            </ul>
            {result.unmatched_cohorts.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="lms-sync-unmatched">
                <p className="inline-flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> {result.unmatched_cohorts.length} cohort name{result.unmatched_cohorts.length === 1 ? '' : 's'} not matched</p>
                <p className="mt-0.5">{result.unmatched_cohorts.map((u) => `${u.name} (${u.rows})`).join(', ')} — create these cohorts or rename them, then paste again.</p>
              </div>
            )}
            {result.skipped > 0 && <p className="text-xs text-slate-500">{result.skipped} rows skipped (no email/id or no cohort).</p>}
          </div>
        )}
        <DialogFooter>
          {!result ? (
            <Button size="sm" onClick={sync} disabled={busy || !parsed || !!parsed.error} data-testid="lms-sync-run">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Route {preview ? preview.routed : (parsed?.rows?.length || 0)} learners</Button>
          ) : (
            <><Button variant="outline" size="sm" onClick={reset} data-testid="lms-sync-again">Paste another</Button><Button size="sm" onClick={() => onOpenChange(false)} data-testid="lms-sync-done">Done</Button></>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
